import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import debounce from "lodash.debounce";
import { useResize } from "../../hooks";
import loadable from "@loadable/component";

const LoadableMapboxGLComponent = loadable(async () => {
    const { default: ReactMapboxGl } = await import(/* webpackChunkName: "ReactMapboxGl" */ "react-mapbox-gl");
    const MapboxGLComponent = ReactMapboxGl({
        accessToken: "pk.eyJ1IjoidG5iM2siLCJhIjoiY2ptNm14bHQ2MWNubzN3bjJmZjJxOWlwOCJ9.JM2XrfZHVM26uw2bQAjZsA",
    });
    return MapboxGLComponent;
});
const LoadableMarker = loadable(async () => {
    const { Marker } = await import(/* webpackChunkName: "ReactMapboxGl" */ "react-mapbox-gl");
    return Marker;
});

function useMapSize(mapRef) {
    const [mapSize, setMapSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        function handleResize() {
            const { width, height } = mapRef.current.container.getBoundingClientRect();
            setMapSize({
                width,
                height,
            });
        }
        const debouncedHandleResize = debounce(handleResize, 300);
        window.addEventListener("resize", debouncedHandleResize);
        debouncedHandleResize();
        return () => {
            debouncedHandleResize.flush();
            window.removeEventListener("resize", debouncedHandleResize);
        };
    }, [mapRef]);

    return mapSize;
}

function fitBounds({ marker, onViewportChange }) {
    const { longitude, latitude } = marker;
    onViewportChange({
        zoom: 11,
        latitude,
        longitude,
    });
}

export const useVhCssVar = mapRef => {
    const mapboxInstance = mapRef?.current?.state?.map;
    useResize(
        useCallback(() => {
            if (mapboxInstance) {
                setTimeout(() => {
                    mapboxInstance.resize();
                }, 5);
            }
            let vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty("--vh", `${vh}px`);
        }, [mapboxInstance]),
    );
};

const flyToOptions = {
    padding: {
        top: 38,
        bottom: 44 + 24,
    },
};
const flyToOptionsWithoutWebsite = {
    padding: {
        top: 38,
    },
};

const AtlasStopMap = function AtlasStopMap(props) {
    const { viewport, onViewportChange, height, marker, website } = props;
    const { longitude, latitude, id } = marker;
    const coordinates = [longitude, latitude];
    const mapRef = useRef();
    const mapSize = useMapSize(mapRef);
    const _flyToOptions = website ? flyToOptions : flyToOptionsWithoutWebsite;
    //HACK: force mapbox to use flyToOptions
    const center = mapRef?.current ? [viewport.longitude, viewport.latitude] : [-78.328043, 45.958913];
    const zoom = [viewport.zoom];
    const containerStyle = {
        height,
        width: "100%",
    };
    const reCenter = () => fitBounds({ marker, onViewportChange });
    const onMapboxViewportChanges = useMemo(
        () =>
            debounce(mapboxInstance => {
                const zoom = mapboxInstance.getZoom();
                const { lng: longitude, lat: latitude } = mapboxInstance.getCenter();
                onViewportChange({
                    ...viewport,
                    longitude,
                    latitude,
                    zoom,
                });
            }, 200),
        [viewport, onViewportChange],
    );

    useVhCssVar(mapRef);

    //Recenter on map resize
    useEffect(() => {
        if (!mapSize.width || !mapSize.height) {
            return;
        }
        reCenter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fitBounds, mapSize]);

    return (
        <>
            <LoadableMapboxGLComponent
                fallback={<div style={containerStyle}></div>}
                ref={mapRef}
                flyToOptions={_flyToOptions}
                zoom={zoom}
                movingMethod="jumpTo"
                center={center}
                containerStyle={containerStyle}
                onMoveEnd={onMapboxViewportChanges}
                onZoomEnd={onMapboxViewportChanges}
                onPitchEnd={onMapboxViewportChanges}
                // eslint-disable-next-line
                style="mapbox://styles/ocreurer/ckmlzz4pu3a5r17po0laaadgk"
            >
                <LoadableMarker className="trpr-atlas-stop-modal__map-marker" key={id} coordinates={coordinates}>
                    <span>
                        <svg width="32" height="38" viewBox="0 0 32 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g filter="url(#filter0_d)">
                                <path
                                    d="M28 14.94C28 21.3108 22.9855 26.9806 16.6695 32.7409C16.2907 33.0864 15.7093 33.0864 15.3305 32.7409C9.0145 26.9806 4 21.3108 4 14.94C4 8.34574 9.37258 3 16 3C22.6274 3 28 8.34574 28 14.94Z"
                                    fill="#504CF3"
                                />
                            </g>
                            <path
                                d="M20 14.94C20 17.1381 18.2091 18.92 16 18.92C13.7909 18.92 12 17.1381 12 14.94C12 12.7419 13.7909 10.96 16 10.96C18.2091 10.96 20 12.7419 20 14.94Z"
                                fill="#D3D2FC"
                            />
                            <defs>
                                <filter
                                    id="filter0_d"
                                    x="0"
                                    y="0"
                                    width="32"
                                    height="38"
                                    filterUnits="userSpaceOnUse"
                                    colorInterpolationFilters="sRGB"
                                >
                                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                    <feColorMatrix
                                        in="SourceAlpha"
                                        type="matrix"
                                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                    />
                                    <feOffset dy="1" />
                                    <feGaussianBlur stdDeviation="2" />
                                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
                                </filter>
                            </defs>
                        </svg>
                    </span>
                </LoadableMarker>
            </LoadableMapboxGLComponent>
        </>
    );
};

AtlasStopMap.defaultProps = {
    height: "300px",
    marker: null,
    viewport: {
        zoom: 11,
    },
    onViewportChange: () => {},
};

AtlasStopMap.propTypes = {
    marker: PropTypes.shape({
        longitude: PropTypes.number.isRequired,
        latitude: PropTypes.number.isRequired,
    }),
    viewport: PropTypes.shape({
        longitude: PropTypes.number.isRequired,
        latitude: PropTypes.number.isRequired,
        zoom: PropTypes.number,
    }).isRequired,
    onViewportChange: PropTypes.func,
};

export default AtlasStopMap;
